import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ApisEnum } from "Data/enums/Apis";
import { QUERY_KEY_MOVE_FILES_LIST } from "Data/keys/QueryKeys";
import ObrasApi from "Data/Obras.api";
import { IListFilesRequest } from "Data/interfaces/ListagemArquivos/IListFilesRequest";
import { IFileData } from "Data/interfaces/ListagemArquivos/IListFilesResponse";
import { IUseQueryFilesReturn } from "Data/interfaces/ListagemArquivos/IListFiles";
import { Mixpanel } from "Utils/MixPanel";
import toastHandler from "Utils/toastHandler";
import { DEFAULT_OBSOLETE_FOLDER_ID } from "Pages/ListagemArquivos/Modals/MoveFilesModal/components/MoveFilesModalListChildren";

export const useQueryMoveFileList = (request: IListFilesRequest) => {
  const { data, refetch, isLoading, isFetching, error } = useQuery({
    queryKey: [QUERY_KEY_MOVE_FILES_LIST, Number(request.constructionSiteId), request.folderId],
    staleTime: 60 * 5 * 1000, // 5 minutes
    enabled: !!request.constructionSiteId && !!request.folderId && request.folderId !== DEFAULT_OBSOLETE_FOLDER_ID,
    retry: 1,
    retryDelay: 1000,
    queryFn: async () => {
      const { userInfoTracking, currentTenantTracking, tenantIds } = request;
      delete request.userInfoTracking;
      delete request.currentTenantTracking;
      delete request.tenantIds;

      const beforeApiCall = dayjs();
      const isGoogleDriveExperimentOnTrmt = request.api === ApisEnum.GoogleDrive;

      const apiCall = isGoogleDriveExperimentOnTrmt
        ? ObrasApi.listGoogleDriveFiles
        : ObrasApi.listFiles;

      const requestApi: IListFilesRequest = {
        ...request,
        loadObsoleteFiles: false,
      };
      if (isGoogleDriveExperimentOnTrmt) {
        requestApi.googleDriveExperiment = 'test';
      }
      const response = await apiCall(requestApi);

      const apiResponseTime = dayjs().diff(beforeApiCall, 'milliseconds');

      Mixpanel.track({
        name: 'FILE_LIST_API_CALL',
        props: {
          elapsed: apiResponseTime,
          googleDriveExperiment: requestApi.googleDriveExperiment,
          api: requestApi.api,
        },
        userInfo: userInfoTracking,
        currentListTenant: currentTenantTracking,
        tenantIds,
      });

      let files: IFileData[] = [];
      if (
        response.data.data?.isObsoleteFiles &&
        response.data.data?.Files &&
        response.data.data.Files?.length > 0
      ) {
        for (const file of response.data.data.Files) {
          files.push({ ...file, Versions: [] });
          if (file?.Versions && file.Versions?.length > 0) {
            for (const olderFiles of file.Versions) {
              files.push(olderFiles);
            }
          }
        }
      } else {
        files = response.data.data.Files;
      }

      const filesList: IFileData[] = files;
      for (const fileRow of filesList) {
        if (!fileRow.RenderingSignalR) {
          fileRow.RenderingSignalR = {
            etapa: 0,
            progress: 0,
            fileIdentifier: fileRow.Identifier.ApiId || fileRow.FileInfo.ApiId,
          };
        }
      }

      const returnResponse = {
        ListFiles: filesList,
        BreadCrumbItem: response.data.data.BreadCrumbItem,
        ObsoleteFolderId: response.data.data.ObsoleteFolderId,
        ConstructionSite: response.data.data.ConstructionSite,
        CloudServiceFolderUrl: response.data.data.CloudServiceFolderUrl,
      } as IUseQueryFilesReturn;
      if (response.data.data.BreadCrumbItem) {
        returnResponse.BreadCrumbItem = response.data.data.BreadCrumbItem;
      }
      if (response.data.data.ObsoleteFolderId) {
        returnResponse.ObsoleteFolderId = response.data.data.ObsoleteFolderId;
      }
      if (response.data.data.ConstructionSite) {
        returnResponse.ConstructionSite = response.data.data.ConstructionSite;
      }
      if (response.data.data.CloudServiceFolderUrl) {
        returnResponse.CloudServiceFolderUrl = response.data.data.CloudServiceFolderUrl;
      }
      return returnResponse;
    },
  });

  useEffect(() => {
    if (!data && !isFetching && error) {
      Mixpanel.track({
        name: 'ERROR_FILE_LIST_API_CALL',
        props: {
          googleDriveExperiment: request.googleDriveExperiment,
          api: request.api,
        },
        userInfo: request.userInfoTracking,
        currentListTenant: request.currentTenantTracking,
        tenantIds: request.tenantIds,
      });

      const msg = 'Erro ao listar arquivos';
      const errorApi = (error as any).errors ? (error as any).errors[0]?.Message || msg : msg;
      toastHandler.showError(errorApi);
    }
  }, [data, error, isFetching, request]);

  const dataDefault = useMemo(() => {
    return {
      ListFiles: [],
      BreadCrumbItem: null,
      ObsoleteFolderId: '',
      CloudServiceFolderUrl: null,
      ConstructionSite: null,
      LastUpdateDate: null,
    };
  }, []);

  return {
    data: data || dataDefault,
    refetch,
    isLoading,
    isFetching,
    error,
  };
};
